import React from 'react';
import { AUDIO_SHAPES, AUDIO_COLORS, LANGUAGE_FLAGS, COMMON_EMOJIS } from '@/lib/constants';

// BADGE VISUAL - Forma con borde de color + emoji + bandera de fondo
export const AudioVisualBadge = ({ shape, color, emoji, language, size = 'md' }) => {
  const sizes = {
    xs: { w: 36, stroke: 1.5, emoji: 'text-sm', flag: 'text-base' },
    sm: { w: 48, stroke: 2, emoji: 'text-lg', flag: 'text-xl' },
    md: { w: 64, stroke: 3, emoji: 'text-2xl', flag: 'text-3xl' },
    lg: { w: 96, stroke: 4, emoji: 'text-4xl', flag: 'text-5xl' }
  };

  const cfg = sizes[size];
  const colorData = AUDIO_COLORS[color] || AUDIO_COLORS.blue;
  const langData = LANGUAGE_FLAGS[language] || LANGUAGE_FLAGS.ES;
  const s = cfg.w;
  const half = s / 2;

  // SVG paths for each shape
  const renderShape = () => {
    const fill = `${colorData.hex}15`; // Very light fill
    const stroke = colorData.hex;
    const sw = cfg.stroke;

    switch (shape) {
      case 'triangle':
        return (
          <polygon 
            points={`${half},${sw} ${s-sw},${s-sw} ${sw},${s-sw}`}
            fill={fill}
            stroke={stroke}
            strokeWidth={sw}
            strokeLinejoin="round"
          />
        );
      case 'triangle_down':
        return (
          <polygon 
            points={`${sw},${sw} ${s-sw},${sw} ${half},${s-sw}`}
            fill={fill}
            stroke={stroke}
            strokeWidth={sw}
            strokeLinejoin="round"
          />
        );
      case 'diamond':
        return (
          <polygon 
            points={`${half},${sw} ${s-sw},${half} ${half},${s-sw} ${sw},${half}`}
            fill={fill}
            stroke={stroke}
            strokeWidth={sw}
            strokeLinejoin="round"
          />
        );
      case 'oval':
        return (
          <ellipse 
            cx={half}
            cy={half}
            rx={half - sw}
            ry={(half - sw) * 0.7}
            fill={fill}
            stroke={stroke}
            strokeWidth={sw}
          />
        );
      case 'hexagon':
        const hx = s * 0.25;
        return (
          <polygon 
            points={`${hx},${sw} ${s-hx},${sw} ${s-sw},${half} ${s-hx},${s-sw} ${hx},${s-sw} ${sw},${half}`}
            fill={fill}
            stroke={stroke}
            strokeWidth={sw}
            strokeLinejoin="round"
          />
        );
      case 'alert':
        return (
          <>
            <polygon 
              points={`${half},${sw} ${s-sw},${s-sw} ${sw},${s-sw}`}
              fill={fill}
              stroke={stroke}
              strokeWidth={sw}
              strokeLinejoin="round"
            />
          </>
        );
      case 'circle':
        return (
          <circle 
            cx={half}
            cy={half}
            r={half - sw}
            fill={fill}
            stroke={stroke}
            strokeWidth={sw}
          />
        );
      case 'rectangle':
        return (
          <rect 
            x={sw}
            y={s * 0.2}
            width={s - sw * 2}
            height={s * 0.6}
            rx="4"
            fill={fill}
            stroke={stroke}
            strokeWidth={sw}
          />
        );
      case 'square':
      default:
        return (
          <rect 
            x={sw}
            y={sw}
            width={s - sw * 2}
            height={s - sw * 2}
            rx="4"
            fill={fill}
            stroke={stroke}
            strokeWidth={sw}
          />
        );
    }
  };

  return (
    <div 
      className="relative flex items-center justify-center"
      style={{ width: s, height: s }}
      title={`${AUDIO_SHAPES[shape]?.name || shape} - ${colorData.name}`}
      data-testid="audio-visual-badge"
    >
      {/* SVG Shape */}
      <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`} className="absolute">
        {renderShape()}
      </svg>
      
      {/* Flag as subtle background */}
      <span 
        className={`absolute ${cfg.flag} opacity-20 select-none`}
        style={{ zIndex: 1 }}
      >
        {langData.flag}
      </span>
      
      {/* Emoji on top */}
      <span 
        className={`relative ${cfg.emoji} z-10`}
        style={{ textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}
      >
        {emoji}
      </span>
    </div>
  );
};

// SELECTOR DE FORMA
export const ShapeSelector = ({ selected, onSelect }) => {
  return (
    <div className="grid grid-cols-3 gap-2">
      {Object.entries(AUDIO_SHAPES).map(([key, shape]) => (
        <button
          key={key}
          onClick={() => onSelect(key)}
          className={`p-3 rounded-lg text-center transition-all ${
            selected === key
              ? 'bg-indigo-100 ring-2 ring-indigo-500'
              : 'bg-gray-50 hover:bg-gray-100'
          }`}
        >
          <span className="text-2xl block mb-1">{shape.icon}</span>
          <span className="text-xs text-gray-600">{shape.name}</span>
        </button>
      ))}
    </div>
  );
};

// SELECTOR DE COLOR
export const ColorSelector = ({ selected, onSelect }) => {
  return (
    <div className="flex flex-wrap gap-2">
      {Object.entries(AUDIO_COLORS).map(([key, color]) => (
        <button
          key={key}
          onClick={() => onSelect(key)}
          className={`w-10 h-10 rounded-full transition-all ${
            selected === key
              ? 'ring-2 ring-offset-2 ring-gray-400 scale-110'
              : 'opacity-80 hover:opacity-100'
          }`}
          style={{ backgroundColor: color.hex }}
          title={color.name}
        />
      ))}
    </div>
  );
};

// SELECTOR DE EMOJI
export const EmojiSelector = ({ selected, onSelect }) => {
  return (
    <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
      {COMMON_EMOJIS.map((emoji) => (
        <button
          key={emoji}
          onClick={() => onSelect(emoji)}
          className={`w-9 h-9 rounded-lg flex items-center justify-center text-lg transition-all ${
            selected === emoji
              ? 'bg-indigo-100 ring-2 ring-indigo-500'
              : 'bg-gray-50 hover:bg-gray-100'
          }`}
        >
          {emoji}
        </button>
      ))}
    </div>
  );
};

// SELECTOR DE IDIOMA
export const LanguageSelector = ({ selected, onSelect }) => {
  return (
    <div className="flex flex-wrap gap-2">
      {Object.entries(LANGUAGE_FLAGS).map(([code, lang]) => (
        <button
          key={code}
          onClick={() => onSelect(code)}
          className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 transition-all ${
            selected === code
              ? 'bg-indigo-100 ring-2 ring-indigo-500'
              : 'bg-gray-50 hover:bg-gray-100'
          }`}
        >
          <span>{lang.flag}</span>
          <span>{code}</span>
        </button>
      ))}
    </div>
  );
};
